import { useState } from "react";
import { Lightbulb, X } from "lucide-react";

// Warm, plain-English "what this is + what to do" for every portal page.
// Written for a first-time owner with zero tech background. Dismissible per page.
const GUIDES = {
  overview: {
    title: "Welcome to your home base",
    body: "Everything about your business lives here — your projects, your plan, your invoices, and your progress. Tap any card to jump in. New here? Start with your Business Plan or your Business Profile, and we'll take it from there.",
  },
  dna: {
    title: "This is your business's memory",
    body: "The more you tell us here, the smarter everything we build for you becomes — you teach us once, and we use it everywhere. Green means we've got it; grey means it's still missing. Tap any item to add or update it. There are no wrong answers, and you can change anything later.",
  },
  plan: {
    title: "Your business plan, built from your answers",
    body: "No blank page to stare at. Pick your goal at the top, open each section, and let us write the first draft for you. Read it over, tweak anything that isn't quite you, then download it or send it to us to polish. Don't worry about the money parts — we'll walk you through them in plain words.",
  },
  roadmap: {
    title: "Your step-by-step path to growth",
    body: "This shows what to do next, and in what order, so you're never left guessing. Work down the list at your own pace — every step moves your business forward.",
  },
  projects: {
    title: "Everything we're building together",
    body: "All your projects in one place. Tap one to see its progress, message us, review proposals, and pay invoices. We'll keep you posted every step of the way.",
  },
  invoices: {
    title: "Your invoices, made simple",
    body: "See what's paid, what's due, and pay securely in a couple of taps. Have a question about a charge? Just message us — we're always happy to explain.",
  },
  downloads: {
    title: "Your files, ready when you are",
    body: "Anything you've purchased or we've delivered shows up here for you to download, anytime you need it.",
  },
  mybot: {
    title: "Your very own AI assistant",
    body: "Set it up once and it can answer visitor questions and capture leads for you — day or night. We'll guide you through switching it on.",
  },
  activity: {
    title: "A simple history of what's happening",
    body: "Every update, message, and milestone for your business, in one tidy timeline — so nothing ever slips through the cracks.",
  },
};

const STORE_KEY = "atf_portal_guides_dismissed";
const readDismissed = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "[]"); } catch { return []; }
};

export const PortalGuide = ({ view }) => {
  const guide = GUIDES[view];
  const [dismissed, setDismissed] = useState(() => readDismissed().includes(view));
  if (!guide || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      const next = Array.from(new Set([...readDismissed(), view]));
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch (e) {}
  };

  return (
    <div className="flex items-start gap-3 bg-gold/10 border border-gold/25 px-5 py-4 mb-8" data-testid={`portal-guide-${view}`}>
      <span className="w-9 h-9 shrink-0 flex items-center justify-center border border-gold/40 text-gold"><Lightbulb size={18} /></span>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium mb-0.5">{guide.title}</p>
        <p className="text-gray-300 text-sm leading-relaxed">{guide.body}</p>
      </div>
      <button onClick={dismiss} data-testid={`portal-guide-dismiss-${view}`} aria-label="Got it, hide this tip"
        className="shrink-0 text-gray-400 hover:text-white transition-colors" title="Got it">
        <X size={16} />
      </button>
    </div>
  );
};

export default PortalGuide;
